import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type AdminStats = components['schemas']['AdminStatsRead']
export type AttentionBusiness = components['schemas']['AttentionBusinessRead']
export type AttentionSignal = components['schemas']['AttentionSignalRead']
export type AttentionBucket = components['schemas']['AttentionBucket']
export type BusinessDetail = components['schemas']['AdminBusinessDetailRead']
export type BusinessNote = components['schemas']['BusinessNoteRead']

type NotesPage = components['schemas']['Page_BusinessNoteRead_']
export type AttentionPage = components['schemas']['Page_AttentionBusinessRead_']

export const ATTENTION_PAGE_SIZE = 20

export function useAdminStats(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => (await api.get<AdminStats>('/admin/stats')).data,
    enabled,
  })
}

export function useAttention(params: {page: number; search: string}, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-attention', params],
    queryFn: async () =>
      (
        await api.get<AttentionPage>('/admin/attention', {
          params: {page: params.page, page_size: ATTENTION_PAGE_SIZE, search: params.search || undefined},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useBusinessDetail(businessId: string | undefined) {
  return useQuery({
    queryKey: ['admin-business-detail', businessId],
    queryFn: async () => (await api.get<BusinessDetail>(`/admin/businesses/${businessId}`)).data,
    enabled: !!businessId,
  })
}

export const NOTES_PAGE_SIZE = 20

export function useBusinessNotes(businessId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['admin-notes', businessId, page],
    queryFn: async () =>
      (
        await api.get<NotesPage>(`/admin/businesses/${businessId}/notes`, {
          params: {page, page_size: NOTES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

export function useAddNote(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (text: string) =>
      (await api.post<BusinessNote>(`/admin/businesses/${businessId}/notes`, {text})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['admin-notes', businessId]}),
  })
}

// El backend audita con `updated_by_user_id`, no con un objeto persona: el quién cae a «auditUnknown».
export type ValorEconomico = {
  year: number
  uvt_cop: number
  smmlv_cop: number | null
  updated_at: string | null
  updated_by_user_id?: string | null
  updated_by?: {name: string; last_name?: string} | null
}

export const VALORES_PAGE_SIZE = 20

export type ValoresEconomicosPage = {
  items: ValorEconomico[]
  total: number
  page: number
  page_size: number
}

export function useValoresEconomicos(enabled: boolean, page = 1) {
  return useQuery({
    queryKey: ['admin-valores-economicos', page],
    queryFn: async () =>
      (
        await api.get<ValoresEconomicosPage>('/admin/valores-economicos', {
          params: {page, page_size: VALORES_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useUpsertValorEconomico() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {year: number; uvt_cop: number; smmlv_cop?: number | null}) =>
      (
        await api.put<ValorEconomico>(`/admin/valores-economicos/${input.year}`, {
          uvt_cop: input.uvt_cop,
          smmlv_cop: input.smmlv_cop ?? null,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['admin-valores-economicos']}),
  })
}
